import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Option, StatusOption } from '../../../types/common';
import './StatusSelector.scss';
import { CORE_STATUSES } from '@Constants/constants';

interface StatusSelectorProps {
    selectedStatusId: number;
    statuses: StatusOption[];
    onChange: (statusId: number) => void;
    disabled?: boolean;
    className?: string;
    isPastDue?: boolean;
}

interface StatusColors {
    dotColor: string;
    bgColor: string;
    textColor: string;
    hoverBgColor: string;
    borderColor: string;
}

const StatusSelector = ({
    selectedStatusId,
    statuses,
    onChange,
    isPastDue,
    disabled = false,
    className = ''
}: StatusSelectorProps) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
    const [optimisticStatusId, setOptimisticStatusId] = useState<number | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside, or when the page scrolls/resizes
    // (the menu is position: fixed and its top/right are only computed on open,
    // so without this it detaches from the button and floats over neighbouring cards)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // menu is portaled to document.body, so it's outside dropdownRef — check it too
            const insideTrigger = dropdownRef.current?.contains(target);
            const insideMenu = menuRef.current?.contains(target);
            if (!insideTrigger && !insideMenu) {
                setShowDropdown(false);
            }
        };

        const handleCloseOnViewportChange = (event: Event) => {
            if (menuRef.current?.contains(event.target as Node)) return;
            setShowDropdown(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        // capture: true so this fires for scrolls on any scrollable ancestor, not just window
        window.addEventListener('scroll', handleCloseOnViewportChange, true);
        window.addEventListener('resize', handleCloseOnViewportChange);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleCloseOnViewportChange, true);
            window.removeEventListener('resize', handleCloseOnViewportChange);
        };
    }, []);

    // Get status colors based on Core Status ID (not customizable status names)
    const getStatusColors = (status: StatusOption): StatusColors => {
        // Use coreStatusId for consistent coloring regardless of custom status names
        const coreStatusId = status.coreStatusId;

        // Core Status IDs mapping (these should be consistent across all customers)
        switch (coreStatusId) {

            case CORE_STATUSES.NotStarted:
                return {
                    dotColor: '#CDCCCC',      // Not Due gray
                    bgColor: '#f5f5f5',      // Light gray background
                    textColor: '#6b7280',     // Dark gray text
                    hoverBgColor: '#e5e7eb', // Darker gray hover
                    borderColor: '#CDCCCC'
                };

            case CORE_STATUSES.InProgress: // In Progress / Active / Partially Complete
                return {
                    dotColor: '#82b1ff',      // In Progress light blue
                    bgColor: '#e3f2fd',      // Light blue background
                    textColor: '#1565c0',     // Dark blue text
                    hoverBgColor: '#bbdefb', // Darker blue hover
                    borderColor: '#82b1ff'
                };

            case CORE_STATUSES.Complete: // Complete / Done
                return {
                    dotColor: '#40AF32',      // Complete green
                    bgColor: '#e8f5e8',      // Light green background
                    textColor: '#2e7d32',     // Dark green text
                    hoverBgColor: '#c8e6c9', // Darker green hover
                    borderColor: '#40AF32'
                };

            default: // N/A or unknown status
                return {
                    dotColor: '#ACE874',      // Not applicable light green
                    bgColor: '#f1f8e9',      // Very light green background
                    textColor: '#558b2f',     // Dark green text
                    hoverBgColor: '#dcedc8', // Darker green hover
                    borderColor: '#ACE874'
                };
        }
    };

    // Clear the optimistic override once the prop catches up to it
    useEffect(() => {
        if (optimisticStatusId !== null && selectedStatusId === optimisticStatusId) {
            setOptimisticStatusId(null);
        }
    }, [selectedStatusId, optimisticStatusId]);

    const effectiveStatusId = optimisticStatusId ?? selectedStatusId;
    const selectedStatus = statuses.find(s => s.id === effectiveStatusId);
    const selectedColors = selectedStatus ? getStatusColors(selectedStatus) : getStatusColors({ id: 0, name: 'default', key: 0, value: '0' } as Option);

    // Check if status is "Not Started" based on Core Status ID, not name
    const isNotStarted = selectedStatus && selectedStatus.coreStatusId === CORE_STATUSES.NotStarted;
    const isPastDueNotStarted = isPastDue && isNotStarted;

    const buttonColors = isPastDueNotStarted ? {
        bgColor: '#ffebee',
        textColor: '#D20005',
        borderColor: '#D20005',
        hoverBgColor: '#ffcdd2'
    } : {
        bgColor: selectedColors.bgColor,
        textColor: selectedColors.textColor,
        borderColor: 'transparent',
        hoverBgColor: selectedColors.hoverBgColor
    };

    return (
        <div className={`status-selector ${className}`} ref={dropdownRef} style={{ position: 'relative' }}>
            <button
                ref={buttonRef}
                onClick={() => {
                    if (disabled) return;
                    if (!showDropdown && buttonRef.current) {
                        const rect = buttonRef.current.getBoundingClientRect();
                        setDropdownPos({
                            top: rect.bottom + 8,
                            right: window.innerWidth - rect.right,
                        });
                    }
                    setShowDropdown(!showDropdown);
                }}
                disabled={disabled}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: `1px solid ${buttonColors.borderColor}`,
                    backgroundColor: buttonColors.bgColor,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Greycliff',
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '20px',
                    opacity: disabled ? 0.6 : 1,
                    outline: 'none',
                    whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.backgroundColor = buttonColors.hoverBgColor;
                        e.currentTarget.style.boxShadow = '0px 2px 6px rgba(0,0,0,0.08)';
                    }
                }}
                onMouseOut={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.backgroundColor = buttonColors.bgColor;
                        e.currentTarget.style.boxShadow = 'none';
                    }
                }}
                onFocus={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${selectedColors.borderColor}20`;
                    }
                }}
                onBlur={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.boxShadow = 'none';
                    }
                }}
            >
                {/* Past Due Warning Icon - Removed per user request */}

                {/* Status Color Dot */}
                <div
                    style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: selectedColors.dotColor,
                        flexShrink: 0,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}
                />

                {/* Status Text */}
                <span style={{ color: buttonColors.textColor }}>
                    {selectedStatus?.name || 'Select Status'}
                </span>

                {/* Dropdown Arrow */}
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{
                        color: buttonColors.textColor,
                        transition: 'transform 0.2s ease',
                        transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                >
                    <path
                        d="M3 5L6 8L9 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {showDropdown && dropdownPos && createPortal(
                <div ref={menuRef} style={{
                    position: 'fixed',
                    top: dropdownPos.top,
                    right: dropdownPos.right,
                    marginTop: '0',
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                    padding: '4px',
                    zIndex: 20001,
                    minWidth: '200px',
                    maxHeight: '212px',
                    overflowY: 'auto'
                }}>
                    {statuses.map((status) => {
                        const statusColors = getStatusColors(status);
                        const isSelected = status.id === effectiveStatusId;

                        return (
                            <button
                                key={status.id}
                                onClick={() => {
                                    setOptimisticStatusId(status.id);
                                    onChange(status.id);
                                    setShowDropdown(false);
                                }}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '10px 16px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: isSelected ? '#f8f9fa' : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    fontFamily: 'Greycliff',
                                    fontSize: '14px',
                                    fontWeight: isSelected ? '500' : '400',
                                    lineHeight: '20px',
                                    textAlign: 'left'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = isSelected ? '#f8f9fa' : 'transparent';
                                }}
                            >
                                {/* Color Dot */}
                                <div
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: statusColors.dotColor,
                                        flexShrink: 0
                                    }}
                                />

                                {/* Status Name */}
                                <span style={{ color: '#31363a' }}>
                                    {status.name}
                                </span>
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
};

export default StatusSelector;